<template>
  <div class="row-action-menu-wrapper">
    <div v-if="open"
         class="dropdown-menu show row-action-dropdown"
         ref="menu"
         role="menu"
         :style="{ left: x + 'px', top: y + 'px' }"
         @click.stop>
      <template v-for="btn in visibleButtons">
        <a :key="btn.id || btn.text"
           class="dropdown-item text-1"
           href="javascript:;"
           :title="btn.title"
           @click="handleClick(btn, $event)">
          <i v-if="btn.icon"
             :style="{color: btn.iconColor || 'inherit'}"
             :class="btn.icon"></i> {{ btn.text }}
        </a>
      </template>
      <span class="dropdown-item text-muted" v-if="!visibleButtons.length">Nenhuma ação disponível</span>
    </div>
  </div>
</template>

<script>
import menuBus from '@/services/rowActionMenuBus.js';

// RowActionMenu: renderiza um subconjunto de `actionButtons` (mesma fonte de dados/lógica
// já usada por DataHeader.vue) como um menu de contexto aberto exatamente nas coordenadas do
// clique (x/y em viewport, position:fixed). Quem decide QUANDO abrir/fechar é o pai (via
// prop `open`) — o pai já sabe a sequência certa: selecionar a linha (pipeline existente de
// handleRowClick/changeCheckboxCallback, que recalcula `.visible` em cada botão) e só então
// mostrar o menu.
//
// `padrao` NÃO significa "ação global sem depender de linha selecionada" — é só a marca que
// diz pro DataHeader.vue "renderize este botão no header genérico". Na maioria das telas
// (Avaliação, Cores, etc.) TODO botão usa essa classe, inclusive os que dependem da seleção
// (Aprovar/Editar/Detalhes) — só Veiculos.vue usa classes próprias (veiculo-*/requisicao-*).
// Por isso a exclusão certa é por uma lista pequena de utilitários "sempre visíveis"
// (criar/exportar/imprimir/salvar), não por checar `padrao`.
const CLASSES_UTILITARIAS = ['novo', 'create', 'excel', 'print', 'gestaovista', 'salvar', 'btn-cancelar'];

// Fecha como um dropdown normal do sistema: clique fora ou Esc — não por distância do mouse.
export default {
    props: {
        open: { type: Boolean, default: false },
        x: { type: Number, default: 0 },
        y: { type: Number, default: 0 },
        actionButtons: { type: Array, required: true },
        filterClass: { type: String, default: null }
    },
    computed: {
        visibleButtons() {
            // token exato (não substring): 'veiculo-novo-despesa' não pode ser confundido
            // com o utilitário 'novo' só porque contém esse pedaço no meio do nome
            let list = this.actionButtons.filter(b => {
                let tokens = b.className.split(' ');
                return !CLASSES_UTILITARIAS.some(c => tokens.includes(c));
            });
            // aqui sim é por prefixo/substring mesmo: veiculo-* e requisicao-* não têm
            // um token isolado 'veiculo'/'requisicao', é parte do nome da classe
            if (this.filterClass) {
                list = list.filter(b => b.className.includes(this.filterClass));
            }
            return list.filter(b => b.visible);
        }
    },
    watch: {
        open(isOpen) {
            if (isOpen) {
                menuBus.$emit('menu-aberto', this._uid);
                menuBus.$on('menu-aberto', this.handleOutroMenuAberto);
                this.$nextTick(() => {
                    this.clampPosition();
                    // adia em 1 tick de macrotask pra não fechar sozinho com o mesmo clique
                    // que acabou de abrir (esse clique ainda vai borbulhar até o document)
                    this._attachTimeout = setTimeout(this.startListeningOutsideClick, 0);
                });
            } else {
                menuBus.$off('menu-aberto', this.handleOutroMenuAberto);
                this.stopListeningOutsideClick();
            }
        }
    },
    beforeDestroy() {
        menuBus.$off('menu-aberto', this.handleOutroMenuAberto);
        this.stopListeningOutsideClick();
        clearTimeout(this._attachTimeout);
    },
    methods: {
        handleClick(btn, evt) {
            btn.callback(evt);
            this.$emit('close');
        },
        handleOutroMenuAberto(uid) {
            if (uid !== this._uid) {
                this.$emit('close');
            }
        },
        // evita que o menu nasça parcialmente fora da tela quando o clique acontece
        // perto da borda direita/inferior do viewport
        clampPosition() {
            let el = this.$refs.menu;
            if (!el) {
                return;
            }
            let rect = el.getBoundingClientRect();
            let margem = 8;
            if (rect.right > window.innerWidth) {
                el.style.left = Math.max(margem, window.innerWidth - rect.width - margem) + 'px';
            }
            if (rect.bottom > window.innerHeight) {
                el.style.top = Math.max(margem, window.innerHeight - rect.height - margem) + 'px';
            }
        },
        startListeningOutsideClick() {
            this._onDocClick = (evt) => {
                if (this.$el && !this.$el.contains(evt.target)) {
                    this.$emit('close');
                }
            };
            this._onKeyDown = (evt) => {
                if (evt.key === 'Escape') {
                    this.$emit('close');
                }
            };
            document.addEventListener('click', this._onDocClick);
            document.addEventListener('keydown', this._onKeyDown);
        },
        stopListeningOutsideClick() {
            if (this._onDocClick) {
                document.removeEventListener('click', this._onDocClick);
                this._onDocClick = null;
            }
            if (this._onKeyDown) {
                document.removeEventListener('keydown', this._onKeyDown);
                this._onKeyDown = null;
            }
        }
    }
};
</script>

<style scoped>
/* display:contents tira este wrapper do fluxo de layout por completo — ele só existe
   porque o Vue 2 exige um único nó raiz no template. Sem isso, mesmo vazio, um elemento
   inline-block ainda reserva espaço na caixa de linha (efeito colateral do inline-block),
   empurrando o conteúdo abaixo (ex.: o rodapé de paginação) alguns pixels quando o menu
   abre/fecha. O dropdown filho continua fixo na tela normalmente — position:fixed não
   depende do display do elemento pai. */
.row-action-menu-wrapper {
    display: contents;
}
.row-action-dropdown {
    position: fixed;
    z-index: 1050;
}
</style>
